import { useState, useEffect, useCallback } from "react";
import { Users, Trophy, ChevronDown, ChevronUp, Check, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Poll questions to randomly select from (one per broadcast)
const POLL_QUESTIONS = [
  "Will this game go to overtime?",
  "Will the score stay within one possession the whole game?",
  "Does the first quarter feel fast or slow?",
  "Who controls the pace of the game?",
  "Will the lead change in the second half?",
  "Who has the nicer jersey?",
  "Is the stadium atmosphere electric or calm?",
  "Which fanbase is more passionate?",
  "Which student section brings more energy?",
  "Does the crowd impact this game?",
  "What team mascot wins in a fight?",
  "Do you trust the play-calling so far?",
  "Which coach made the better adjustment?",
  "Which team looks more prepared?",
  "Who has the most exciting player to watch?"
];

// Get a deterministic random question based on broadcast ID
const getQuestionForBroadcast = (broadcastId) => {
  if (!broadcastId) return POLL_QUESTIONS[0];
  let hash = 0;
  for (let i = 0; i < broadcastId.length; i++) {
    hash = ((hash << 5) - hash) + broadcastId.charCodeAt(i);
    hash = hash & hash;
  }
  return POLL_QUESTIONS[Math.abs(hash) % POLL_QUESTIONS.length];
};

/**
 * GameHub - Interactive engagement section below the video player
 * Contains: Live Poll, Player of the Game voting
 */
export const GameHub = ({ 
  broadcast, 
  isLive = false,
  homeTeam,
  awayTeam,
  statsUrl  // StatMoose URL for player data
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState("poll");
  
  // Poll state
  const [pollVote, setPollVote] = useState(null);
  const [pollResults, setPollResults] = useState({ option1: 0, option2: 0 });
  const [showPollResults, setShowPollResults] = useState(false);
  const [pollQuestion, setPollQuestion] = useState("");
  
  // Player of the Game state
  const [players, setPlayers] = useState({ home: [], away: [] });
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [potgLoading, setPotgLoading] = useState(false);
  const [potgVotes, setPotgVotes] = useState({});

  // Set poll question based on broadcast ID
  useEffect(() => {
    if (broadcast?.id) {
      setPollQuestion(getQuestionForBroadcast(broadcast.id));
    }
  }, [broadcast?.id]);

  // Fetch players from StatMoose via ASN backend
  useEffect(() => {
    const fetchPlayers = async () => {
      if (!broadcast?.id) return;
      
      try {
        // First try ASN backend endpoint (uses StatMoose API)
        const res = await fetch(`${API}/broadcasts/statmoose/players/${broadcast.id}`);
        if (res.ok) {
          const data = await res.json();
          if (data.home?.length > 0 || data.away?.length > 0) {
            setPlayers({
              home: data.home || [],
              away: data.away || []
            });
            return;
          }
        }
        
        // Fallback: Try direct StatMoose share link if stats_link provided
        if (statsUrl) {
          const shareCode = statsUrl.includes('/') 
            ? statsUrl.split('/').pop() 
            : statsUrl;
          
          // Route through our backend proxy (avoids CORS/firewall blocks)
          const response = await fetch(`${API}/broadcasts/statmoose/proxy/${shareCode}`);
          
          if (response.ok) {
            const data = await response.json();
            if (!data.error) {
              setPlayers({
                home: data.home_player_stats || [],
                away: data.away_player_stats || []
              });
            }
          }
        }
      } catch (e) {
        console.error("Error fetching players:", e);
      }
    };
    
    fetchPlayers();
    // Refresh every 30 seconds during live games
    if (isLive) {
      const interval = setInterval(fetchPlayers, 30000);
      return () => clearInterval(interval);
    }
  }, [broadcast?.id, statsUrl, isLive]);

  // Load poll results from localStorage (simple client-side tracking)
  useEffect(() => {
    if (!broadcast?.id) return;
    const stored = localStorage.getItem(`poll_${broadcast.id}`);
    if (stored) {
      const { vote, results } = JSON.parse(stored);
      setPollVote(vote);
      setPollResults(results);
      setShowPollResults(true);
    }
  }, [broadcast?.id]);

  // Load POTG vote from localStorage
  useEffect(() => {
    if (!broadcast?.id) return;
    const stored = localStorage.getItem(`potg_${broadcast.id}`);
    if (stored) {
      setSelectedPlayer(stored);
    }
    
    // Load vote counts
    const votes = localStorage.getItem(`potg_votes_${broadcast.id}`);
    if (votes) {
      setPotgVotes(JSON.parse(votes));
    }
  }, [broadcast?.id]);

  const votePoll = (option) => {
    if (pollVote) return;
    
    const newResults = {
      ...pollResults,
      [option]: pollResults[option] + 1
    };
    
    setPollVote(option);
    setPollResults(newResults);
    setShowPollResults(true);
    
    // Store in localStorage
    localStorage.setItem(`poll_${broadcast.id}`, JSON.stringify({
      vote: option,
      results: newResults
    }));
  };

  const votePlayerOfGame = (playerId) => {
    if (selectedPlayer) return;
    
    setSelectedPlayer(playerId);
    const newVotes = {
      ...potgVotes,
      [playerId]: (potgVotes[playerId] || 0) + 1
    };
    setPotgVotes(newVotes);
    
    // Store in localStorage
    localStorage.setItem(`potg_${broadcast.id}`, playerId);
    localStorage.setItem(`potg_votes_${broadcast.id}`, JSON.stringify(newVotes));
  };

  const getPollPercentage = (option) => {
    const total = pollResults.option1 + pollResults.option2;
    if (total === 0) return 50;
    return Math.round((pollResults[option] / total) * 100);
  };

  // Generate poll options based on question
  const getPollOptions = () => {
    const q = pollQuestion.toLowerCase();
    if (q.includes("overtime")) return ["Yes", "No"];
    if (q.includes("within one possession")) return ["Yes", "No"];
    if (q.includes("fast or slow")) return ["Fast", "Slow"];
    if (q.includes("pace")) return [homeTeam?.name || "Home", awayTeam?.name || "Away"];
    if (q.includes("lead change")) return ["Yes", "No"];
    if (q.includes("jersey")) return [homeTeam?.name || "Home", awayTeam?.name || "Away"];
    if (q.includes("atmosphere")) return ["Electric", "Calm"];
    if (q.includes("fanbase") || q.includes("student section") || q.includes("energy")) 
      return [homeTeam?.name || "Home", awayTeam?.name || "Away"];
    if (q.includes("crowd impact")) return ["Yes", "No"];
    if (q.includes("mascot")) return [homeTeam?.mascot || homeTeam?.name || "Home", awayTeam?.mascot || awayTeam?.name || "Away"];
    if (q.includes("play-calling")) return ["Yes", "No"];
    if (q.includes("coach") || q.includes("prepared") || q.includes("exciting")) 
      return [homeTeam?.name || "Home", awayTeam?.name || "Away"];
    return ["Yes", "No"];
  };

  const pollOptions = getPollOptions();

  if (!broadcast) return null;

  return (
    <div className="bg-zinc-900/50 border border-white/10 mt-4" data-testid="game-hub">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-orange-500" />
          <span className="font-bold text-white uppercase text-sm">Game Hub</span>
          {isLive && (
            <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded animate-pulse">
              LIVE
            </span>
          )}
        </div>
        {isExpanded ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
      </button>

      {isExpanded && (
        <div className="border-t border-white/10">
          {/* Tab Navigation */}
          <div className="flex border-b border-white/10 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => setActiveTab("poll")}
              className={`flex-1 px-4 py-3 text-xs font-bold uppercase flex items-center justify-center gap-2 transition-colors ${
                activeTab === "poll" 
                  ? "bg-orange-500/20 text-orange-500 border-b-2 border-orange-500" 
                  : "text-zinc-400 hover:text-white"
              }`}
              data-testid="poll-tab"
            >
              <Users className="w-4 h-4" />
              Live Poll
            </button>
            <button
              onClick={() => setActiveTab("potg")}
              className={`flex-1 px-4 py-3 text-xs font-bold uppercase flex items-center justify-center gap-2 transition-colors ${
                activeTab === "potg" 
                  ? "bg-orange-500/20 text-orange-500 border-b-2 border-orange-500" 
                  : "text-zinc-400 hover:text-white"
              }`}
              data-testid="potg-tab"
            >
              <Trophy className="w-4 h-4" />
              Player of the Game
            </button>
          </div>

          {/* Tab Content */}
          <div className="p-4">
            {/* Live Poll Tab */}
            {activeTab === "poll" && (
              <div className="space-y-4" data-testid="poll-content">
                <p className="text-white font-medium text-center">{pollQuestion}</p>
                
                <div className="space-y-2">
                  {/* Option 1 */}
                  <button
                    onClick={() => votePoll("option1")}
                    disabled={!!pollVote}
                    className={`w-full p-3 border text-left transition-all relative overflow-hidden ${
                      pollVote === "option1"
                        ? "border-orange-500 bg-orange-500/20"
                        : pollVote
                          ? "border-zinc-700 bg-zinc-800/50"
                          : "border-zinc-700 bg-zinc-800 hover:border-orange-500"
                    }`}
                    data-testid="poll-option1"
                  >
                    {showPollResults && (
                      <div 
                        className="absolute inset-y-0 left-0 bg-orange-500/20 transition-all"
                        style={{ width: `${getPollPercentage("option1")}%` }}
                      />
                    )}
                    <div className="relative flex items-center justify-between">
                      <span className="text-white font-medium">{pollOptions[0]}</span>
                      {showPollResults && (
                        <span className="text-zinc-400 text-sm">{getPollPercentage("option1")}%</span>
                      )}
                      {pollVote === "option1" && <Check className="w-4 h-4 text-orange-500" />}
                    </div>
                  </button>

                  {/* Option 2 */}
                  <button
                    onClick={() => votePoll("option2")}
                    disabled={!!pollVote}
                    className={`w-full p-3 border text-left transition-all relative overflow-hidden ${
                      pollVote === "option2"
                        ? "border-orange-500 bg-orange-500/20"
                        : pollVote
                          ? "border-zinc-700 bg-zinc-800/50"
                          : "border-zinc-700 bg-zinc-800 hover:border-orange-500"
                    }`}
                    data-testid="poll-option2"
                  >
                    {showPollResults && (
                      <div 
                        className="absolute inset-y-0 left-0 bg-orange-500/20 transition-all"
                        style={{ width: `${getPollPercentage("option2")}%` }}
                      />
                    )}
                    <div className="relative flex items-center justify-between">
                      <span className="text-white font-medium">{pollOptions[1]}</span>
                      {showPollResults && (
                        <span className="text-zinc-400 text-sm">{getPollPercentage("option2")}%</span>
                      )}
                      {pollVote === "option2" && <Check className="w-4 h-4 text-orange-500" />}
                    </div>
                  </button>
                </div>

                {pollVote && (
                  <p className="text-center text-zinc-500 text-xs">
                    Thanks for voting! Results update in real-time.
                  </p>
                )}
              </div>
            )}

            {/* Player of the Game Tab */}
            {activeTab === "potg" && (
              <div className="space-y-4" data-testid="potg-content">
                <p className="text-zinc-400 text-sm text-center">Vote for the Player of the Game</p>
                
                {players.home.length === 0 && players.away.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500">
                    <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Player stats will appear once the game starts</p>
                    {!statsUrl && (
                      <p className="text-xs mt-1">No StatMoose data linked to this broadcast</p>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    {/* Away Team Players */}
                    <div>
                      <p className="text-xs text-zinc-500 uppercase mb-2 font-bold">{awayTeam?.name || "Away"}</p>
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {players.away.slice(0, 5).map((player, idx) => (
                          <button
                            key={`away-${idx}`}
                            onClick={() => votePlayerOfGame(`away-${idx}`)}
                            disabled={!!selectedPlayer}
                            className={`w-full p-2 text-left text-sm transition-all ${
                              selectedPlayer === `away-${idx}`
                                ? "bg-orange-500/20 border border-orange-500"
                                : selectedPlayer
                                  ? "bg-zinc-800/50 border border-zinc-700 opacity-50"
                                  : "bg-zinc-800 border border-zinc-700 hover:border-orange-500"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-white">{player.name || `Player ${idx + 1}`}</span>
                              {selectedPlayer === `away-${idx}` && <Check className="w-3 h-3 text-orange-500" />}
                            </div>
                            <span className="text-xs text-zinc-500">
                              {player.points || 0} PTS, {player.rebounds || 0} REB, {player.assists || 0} AST
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Home Team Players */}
                    <div>
                      <p className="text-xs text-zinc-500 uppercase mb-2 font-bold">{homeTeam?.name || "Home"}</p>
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {players.home.slice(0, 5).map((player, idx) => (
                          <button
                            key={`home-${idx}`}
                            onClick={() => votePlayerOfGame(`home-${idx}`)}
                            disabled={!!selectedPlayer}
                            className={`w-full p-2 text-left text-sm transition-all ${
                              selectedPlayer === `home-${idx}`
                                ? "bg-orange-500/20 border border-orange-500"
                                : selectedPlayer
                                  ? "bg-zinc-800/50 border border-zinc-700 opacity-50"
                                  : "bg-zinc-800 border border-zinc-700 hover:border-orange-500"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-white">{player.name || `Player ${idx + 1}`}</span>
                              {selectedPlayer === `home-${idx}` && <Check className="w-3 h-3 text-orange-500" />}
                            </div>
                            <span className="text-xs text-zinc-500">
                              {player.points || 0} PTS, {player.rebounds || 0} REB, {player.assists || 0} AST
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {selectedPlayer && (
                  <p className="text-center text-zinc-500 text-xs">
                    Your vote has been recorded!
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GameHub;
